<script lang="ts" setup>
import type { EsProduct } from '~/types/elastic'
import type { DataLayerItem } from '~/composables/useDataLayer'
import { AppLink } from '#components'

const props = withDefaults(defineProps<{
  product: EsProduct
  list?: string
  parameter?: string
  dataLayerOverrides?: Partial<DataLayerItem>
}>(), {
  list: 'other',
  parameter: '',
  dataLayerOverrides: undefined,
})

const { product } = toRefs(props)

const hover = ref(false)

const imageSrc = computed(() => {
  let imageSrc = product.value.image

  if (hover.value && product.value.hoverImage) {
    imageSrc = product.value.hoverImage
  }

  return useXcdnImage(imageSrc, 'f=rs:fit:400:400', product.value.name)
})

const prices = computed(() => {
  if (product.value.fromPrice) {
    return {
      regularPrice: product.value.fromPrice,
      finalPrice: product.value.fromPrice,
    }
  }
  return {
    regularPrice: product.value.regularPrice,
    finalPrice: product.value.finalPrice,
  }
})

function mouseOver() {
  hover.value = true
}

function mouseOut() {
  hover.value = false
}

const emit = defineEmits(['productClick'])
const { selectItem } = useDataLayer()

const trackProductClick = ({ product, dataLayerOverrides }: { product: EsProduct, dataLayerOverrides: Record<string, unknown> }) => {
  selectItem(product, dataLayerOverrides)
  emit('productClick', product)
}
</script>

<template>
  <div
    v-if="product"
    class="product-item"
  >
    <div class="product-item__details">
      <h3 class="product-item__name">
        <AppLink
          :to="`${product.url}${parameter}`"
          class="product-item__link"
          :class="{ 'sqzl-click': product?.squeezely }"
          :data-sqzl-personalization-id="product?.squeezely?.personalizationId"
          :data-sqzl-variant-id="product?.squeezely?.variantId"
          :data-sqzl-variant-revision="product?.squeezely?.variantRevision"
          @click="trackProductClick({ product: product, dataLayerOverrides: dataLayerOverrides })"
          @mouseover="mouseOver"
          @mouseout="mouseOut"
        >
          {{ product.name }}
        </AppLink>
        <span class="product-item__brand">
          {{ product.brand?.label }}
        </span>
      </h3>

      <UiPrice
        v-if="prices.regularPrice || prices.finalPrice || product.fromPrice"
        class="product-item__price"
        :prices="prices"
      >
        <template
          v-if="product.fromPrice"
          #prefix
        >
          v.a.
        </template>
      </UiPrice>
      <div
        v-else
        class="product-item__price product-item__price--no-price"
      >
        {{ $t('Price on request') }}
      </div>

      <ProductColorVariant
        v-if="product.sku !== 'giftcard' && !product?.isShowroom && product.isCustomMade === 'Ja'"
        :product="product"
        :color-variants="product?.relatedColorProducts"
        class="product-item__color-variants"
      />
    </div>

    <div class="product-item__labels">
      <ProductLabelSale
        v-if="prices"
        :prices="prices"
      />

      <ProductLabelMarketing :product="product" />
    </div>

    <ProductToggleWishlist
      class="product-item__wishlist"
      :product="product"
    />

    <div class="product-item__visual">
      <img
        class="product-item__image"
        :src="imageSrc"
        width="400"
        height="400"
        loading="lazy"
        :alt="product.name"
      >
    </div>
  </div>
</template>

<style>
@import url("~layers/app/components/Product/Tile.css");
</style>
